<template>
    <mobile-template>

        <!-- HEADER -->
        <div class="flex items-center gap-3 px-4 pt-5 pb-3">
            <a
                class="w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-sm active:bg-gray-50 shrink-0"
                href="/"
            >
                <i class="fas fa-chevron-left text-gray-800"></i>
            </a>
            <span class="text-[13px] font-semibold text-gray-400 flex-1">Accueil</span>
            <span
                v-if="chantier"
                class="text-[12px] font-bold px-3 py-1.5 rounded-full"
                :class="chantier.terminer ? 'bg-green-50 text-green-600' : 'bg-orange-50 text-orange-500'"
            >
                {{ chantier.terminer ? 'Terminé' : 'En cours' }}
            </span>
        </div>

        <!-- LOADING -->
        <div v-if="loading" class="flex justify-center py-24">
            <div class="w-8 h-8 border-2 border-gray-200 border-t-orange-500 rounded-full animate-spin"></div>
        </div>

        <!-- ERREUR -->
        <div v-else-if="error" class="mx-4 bg-red-50 rounded-2xl p-6 text-center">
            <p class="text-red-500 font-bold text-base">Impossible de charger</p>
            <button class="mt-4 px-6 py-3 bg-red-500 text-white font-bold rounded-xl" @click="loadData">
                <i class="fas fa-rotate-right mr-2"></i>Réessayer
            </button>
        </div>

        <!-- CONTENU -->
        <div v-else class="px-4 pb-12 flex flex-col gap-4">

            <!-- ── Infos client ── -->
            <div class="text-center pt-2 pb-1">
                <p class="text-[20px] font-extrabold text-gray-900 tracking-tight capitalize">
                    {{ client?.prenom }} {{ client?.nom }}
                </p>
                <p v-if="adresse" class="text-[13px] text-gray-400 font-medium mt-1 leading-snug">
                    <i class="fas fa-location-dot mr-1 text-gray-300"></i>
                    {{ adresse.adresse1 }}, {{ adresse.code_postal }} {{ adresse.ville }}
                </p>
                <p v-if="adresse?.acces" class="text-[13px] text-orange-500 font-semibold mt-1">
                    <i class="fas fa-key mr-1"></i>{{ adresse.acces }}
                </p>
                <p v-if="client?.telephone" class="text-[13px] text-gray-400 font-medium mt-1">
                    <i class="fas fa-phone mr-1 text-gray-300"></i>{{ client.telephone }}
                </p>
            </div>

            <!-- ── Gros boutons d'action ── -->
            <div class="grid grid-cols-2 gap-3">

                <button
                    class="bg-white rounded-[18px] shadow-sm p-5 flex flex-col items-center gap-3 active:opacity-80"
                    @click="signalAbsence"
                >
                    <div class="w-14 h-14 rounded-[16px] bg-slate-50 flex items-center justify-center">
                        <i class="fas fa-user-slash text-slate-400 text-[22px]"></i>
                    </div>
                    <span class="text-[14px] font-extrabold text-gray-900">Client absent</span>
                </button>

                <a
                    v-if="client?.telephone"
                    :href="`tel:${client.telephone}`"
                    class="bg-white rounded-[18px] shadow-sm p-5 flex flex-col items-center gap-3 active:opacity-80 no-underline"
                >
                    <div class="w-14 h-14 rounded-[16px] bg-slate-50 flex items-center justify-center">
                        <i class="fas fa-phone text-slate-400 text-[22px]"></i>
                    </div>
                    <span class="text-[14px] font-extrabold text-gray-900">Appeler</span>
                </a>
            </div>
            <p class="text-[11px] font-bold text-gray-400 uppercase tracking-[.1em] mt-3">
                <i class="fas fa-route mr-1.5"></i>
                Itinéraire
            </p>
            <div class="grid grid-cols-3">
                <a
                    v-if="adresse"
                    :href="appleMapsUrl"
                    target="_blank"
                    class="flex flex-col items-center gap-2 active:opacity-70 no-underline"
                >
                    <img class="h-16 w-16 object-contain" src="https://storage.termoflow.fr/file/NAkZ5qGrNO" alt="">
                    <span class="text-[12px] font-semibold text-gray-700 text-center leading-tight">Apple Plans</span>
                </a>

                <a
                    v-if="adresse"
                    :href="googleMapsUrl"
                    target="_blank"
                    class="flex flex-col items-center gap-2 active:opacity-70 no-underline"
                >
                    <div class="h-16 w-16 rounded-[14px] bg-white shadow-sm flex items-center justify-center overflow-hidden shrink-0">
                        <img src="https://storage.termoflow.fr/file/3WyA5izHPX" alt="Google Maps" class="h-[42px] w-[42px] object-contain">
                    </div>
                    <span class="text-[12px] font-semibold text-gray-700 text-center leading-tight">Google Maps</span>
                </a>

                <a
                    v-if="adresse"
                    :href="wazeUrl"
                    target="_blank"
                    class="flex flex-col items-center gap-2 active:opacity-70 no-underline"
                >
                    <img class="h-16 w-16 object-contain" src="https://storage.termoflow.fr/file/hCf8v36ZXx" alt="">
                    <span class="text-[12px] font-semibold text-gray-700 text-center leading-tight">Waze</span>
                </a>

            </div>

            <!-- ── Carte interventions ── -->
            <div class="bg-white rounded-[20px] shadow-sm overflow-hidden">

                <div class="px-4 py-3 bg-gray-50 border-b border-gray-100">
                    <p class="text-[11px] font-bold text-gray-400 uppercase tracking-[.1em]">
                        <i class="fas fa-list-check mr-1.5"></i>
                        {{ openInterventions.length }} tâche{{ openInterventions.length > 1 ? 's' : '' }} à réaliser
                    </p>
                </div>

                <!-- Actives -->
                <div
                    v-for="intervention in openInterventions"
                    :key="intervention.id"
                    class="flex items-center gap-3 px-4 py-4 border-b border-gray-50 min-h-[72px]"
                >
                    <div class="flex-1 min-w-0">
                        <p class="text-[15px] font-extrabold text-gray-900 leading-snug">
                            {{ getInterventionNom(intervention) }}
                        </p>
                        <p class="text-[12px] text-gray-400 font-medium mt-0.5">
                            <span v-if="formatDuree(intervention.duree_estimee)">Durée estimée : {{ formatDuree(intervention.duree_estimee) }}</span>
                        </p>
                    </div>
                    <button
                        class="px-4 py-3 rounded-[12px] bg-orange-500 text-[14px] font-extrabold text-white shrink-0 active:bg-orange-600"
                        @click="openStartModal(intervention)"
                    >
                        <template v-if="intervention.statut?.code === 'en_cours'">Reprendre</template>
                        <template v-else>
                            <i class="fas fa-hammer"></i>
                        </template>
                    </button>
                </div>

                <!-- Terminées -->
                <div
                    v-for="intervention in closedInterventions"
                    :key="intervention.id"
                    class="flex items-center gap-3 px-4 py-4 border-b border-gray-50 last:border-b-0 min-h-[72px] opacity-40"
                >
                    <div class="flex-1 min-w-0">
                        <p class="text-sm font-light text-gray-900 leading-snug">
                            {{ getInterventionNom(intervention) }}
                        </p>
                    </div>
                    <span class="text-lg text-slate-400 font-bold shrink-0">
                        <i class="fas fa-check-circle mr-1"></i>
                    </span>
                </div>

            </div>

        </div>

        <!-- ── MODAL DÉMARRAGE ── -->
        <transition name="modal-fade">
            <div
                v-if="startModal.show"
                class="fixed inset-0 z-50 flex items-center justify-center px-5 bg-black/50"
                @click.self="closeStartModal"
            >
                <div class="w-full max-w-sm bg-white rounded-[24px] shadow-2xl overflow-hidden">

                    <!-- En-tête coloré -->
                    <div
                        class="px-5 pt-5 pb-4"
                        :style="{ borderBottom: `3px solid ${getTypeColor(startModal.intervention)}15` }"
                    >
                        <div class="flex items-center justify-between mb-3">
                            <span
                                class="text-[11px] font-bold uppercase tracking-[.08em] px-2.5 py-1 rounded-full"
                                :style="{
                                    color: getTypeColor(startModal.intervention),
                                    background: getTypeColor(startModal.intervention) + '18'
                                }"
                            >
                                {{ getTypeLabel(startModal.intervention) }}
                            </span>
                            <button
                                class="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 active:bg-gray-200"
                                @click="closeStartModal"
                            >
                                <i class="fas fa-xmark text-[13px] text-gray-500"></i>
                            </button>
                        </div>

                        <p class="text-[21px] font-extrabold text-gray-900 tracking-tight leading-tight">
                            {{ getInterventionNom(startModal.intervention) }}
                        </p>

                        <div class="flex flex-wrap gap-2 mt-3">
                            <span
                                v-if="formatDuree(startModal.intervention?.duree_estimee)"
                                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[8px] bg-gray-100 text-[12px] text-gray-600 font-semibold"
                            >
                                <svg class="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                                </svg>
                                {{ formatDuree(startModal.intervention?.duree_estimee) }} estimée
                            </span>
                            <span
                                v-if="startModal.intervention?.passage > 1"
                                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[8px] bg-gray-100 text-[12px] text-gray-500 font-semibold"
                            >
                                Passage {{ startModal.intervention?.passage }}
                            </span>
                        </div>
                    </div>

                    <!-- Note interne -->
                    <div
                        v-if="startModal.intervention?.notes_internes"
                        class="mx-4 mt-4 bg-amber-50 rounded-[12px] p-3.5 border border-amber-100"
                    >
                        <p class="text-[10px] font-bold text-amber-700 uppercase tracking-[.08em] mb-1.5 flex items-center gap-1.5">
                            <svg class="w-3 h-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="12" y1="8" x2="12" y2="12"/>
                                <line x1="12" y1="16" x2="12.01" y2="16" stroke-width="3" stroke-linecap="round"/>
                            </svg>
                            Note interne
                        </p>
                        <p class="text-[13px] text-amber-800 font-medium leading-relaxed">
                            {{ startModal.intervention.notes_internes }}
                        </p>
                    </div>

                    <!-- Actions -->
                    <div class="p-4 flex gap-2.5">
                        <button
                            class="flex-1 py-3.5 rounded-[13px] bg-gray-100 text-[15px] font-bold text-gray-600 active:bg-gray-200"
                            :disabled="startModal.starting"
                            @click="closeStartModal"
                        >
                            Annuler
                        </button>
                        <button
                            class="flex-[2] flex items-center justify-center gap-2 py-3.5 rounded-[13px] bg-orange-500 text-[15px] font-extrabold text-white active:bg-orange-600 disabled:opacity-50"
                            :disabled="startModal.starting"
                            @click="startIntervention"
                        >
                            <span v-if="startModal.starting" class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                            <svg v-else class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                            {{ startModal.starting ? 'Démarrage…' : 'Démarrer' }}
                        </button>
                    </div>

                </div>
            </div>
        </transition>

    </mobile-template>
</template>

<script>
export default {
    name: 'InterventionsList',

    data() {
        return {
            loading: true,
            error: null,
            chantier: null,
            client: null,
            adresse: null,
            interventions: [],
            startModal: {
                show: false,
                intervention: null,
                starting: false,
            },
            typeColors: {
                depannage:       '#ef4444',
                entretien:       '#22c55e',
                installation:    '#3b82f6',
                diagnostic:      '#f59e0b',
                mise_en_service: '#8b5cf6',
            },
            typeLabels: {
                depannage:       'Dépannage',
                entretien:       'Entretien',
                installation:    'Installation',
                diagnostic:      'Diagnostic',
                mise_en_service: 'Mise en service',
            },
        };
    },

    computed: {
        chantierId() { return this.$route.params.chantierId; },

        openInterventions() {
            return this.interventions.filter(i => !this.isTermine(i)).sort(this.byDate);
        },
        closedInterventions() {
            return this.interventions.filter(i => this.isTermine(i)).sort(this.byDate);
        },

        wazeUrl() {
            const a = this.adresse;
            if (!a) return '#';
            if (a.latitude && a.longitude)
                return `https://waze.com/ul?ll=${a.latitude},${a.longitude}&navigate=yes`;
            return `https://waze.com/ul?q=${encodeURIComponent(`${a.adresse1} ${a.code_postal} ${a.ville}`)}&navigate=yes`;
        },
        googleMapsUrl() {
            const a = this.adresse;
            if (!a) return '#';
            const dest = a.latitude && a.longitude
                ? `${a.latitude},${a.longitude}`
                : encodeURIComponent(`${a.adresse1} ${a.code_postal} ${a.ville}`);
            return `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
        },
        appleMapsUrl() {
            const a = this.adresse;
            if (!a) return '#';
            if (a.latitude && a.longitude) {
                return `maps://?ll=${a.latitude},${a.longitude}&q=${encodeURIComponent(a.adresse1 || 'Chantier')}`;
            }
            return `maps://?q=${encodeURIComponent(`${a.adresse1} ${a.code_postal} ${a.ville}`)}`;
        }
    },

    created() { this.loadData(); },

    methods: {
        async loadData() {
            this.loading = true;
            this.error   = null;
            try {
                const res = await this.$request.post(`/chantiers/${this.chantierId}/view`, {});
                this.chantier      = res.chantier ?? res;
                this.client        = res.client;
                this.adresse       = res.adresse;
                this.interventions = res.interventions ?? [];
            } catch (e) {
                this.error = e?.message || 'Erreur réseau';
            } finally {
                this.loading = false;
            }
        },

        openStartModal(intervention) {
            // Reprendre directement si déjà en cours
            if (intervention.statut?.code === 'en_cours') {
                this.$router.push(`/intervention/${intervention.id}`);
                return;
            }
            this.startModal.intervention = intervention;
            this.startModal.show = true;
            this.startModal.starting = false;
        },

        closeStartModal() {
            if (this.startModal.starting) return;
            this.startModal.show = false;
            this.startModal.intervention = null;
        },

        async startIntervention() {
            const intervention = this.startModal.intervention;
            if (!intervention) return;

            this.startModal.starting = true;
            try {
                await this.$request.post(`/interventions/${intervention.id}/demarrer`, {});

                // Mise à jour locale du statut
                const idx = this.interventions.findIndex(i => i.id === intervention.id);
                if (idx !== -1) {
                    this.interventions[idx] = {
                        ...this.interventions[idx],
                        statut: { code: 'en_cours', libelle: 'En cours' },
                    };
                }

                this.startModal.show = false;
                this.$router.push(`/intervention/${intervention.id}`);
            } catch (e) {
                alert(e?.message || 'Impossible de démarrer');
            } finally {
                this.startModal.starting = false;
            }
        },

        goToIntervention(i)  { this.$router.push(`/intervention/${i.id}`); },
        signalAbsence()      { this.$router.push(`/chantier/${this.$route.params.eventId}/absent`); },

        initiales(client) {
            if (!client) return '?';
            return (client.prenom?.[0] ?? '').toUpperCase() + (client.nom?.[0] ?? '').toUpperCase();
        },

        isTermine(i)  { return ['termine', 'facture', 'clos'].includes(i.statut?.code || i.statut); },

        byDate(a, b) {
            if (!a.date_intervention) return 1;
            if (!b.date_intervention) return -1;
            return new Date(a.date_intervention) - new Date(b.date_intervention);
        },

        getTypeColor(i) { return this.typeColors[i?.intervention_type?.type_logique] || '#9ca3af'; },
        getTypeLabel(i) { return this.typeLabels[i?.intervention_type?.type_logique] || 'Intervention'; },
        getInterventionNom(i) { return i?.intervention_type?.nom || 'Intervention'; },

        formatDuree(min) {
            const m = parseInt(min, 10);
            if (!m) return null;
            if (m < 60) return `${m} min`;
            const h = Math.floor(m / 60), r = m % 60;
            return r ? `${h}h${r}` : `${h}h`;
        },
    },
};
</script>

<style scoped>
.modal-fade-enter-active { transition: all 0.4s ease-out; }
.modal-fade-leave-active { transition: all 0.15s ease-in; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; transform: scale(0.95); }
</style>